import { TFunction } from 'i18next';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material';
import ForumIcon from '@mui/icons-material/Forum';
import SettingsIcon from '@mui/icons-material/Settings';
import LayersIcon from '@mui/icons-material/Layers';
import ViewListIcon from '@mui/icons-material/ViewList';
import NotesIcon from '@mui/icons-material/Notes';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CategoryIcon from '@mui/icons-material/Category';
import QueueIcon from '@mui/icons-material/Queue';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PreviewIcon from '@mui/icons-material/Preview';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import GroupsIcon from '@mui/icons-material/Groups';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import ImageIcon from '@mui/icons-material/Image';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import InsightsIcon from '@mui/icons-material/Insights';
import HubIcon from '@mui/icons-material/Hub';
import KeyIcon from '@mui/icons-material/Key';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import WarningIcon from '@mui/icons-material/Warning';
import GroupIcon from '@mui/icons-material/Group';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import ChecklistIcon from '@mui/icons-material/Checklist';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FeedIcon from '@mui/icons-material/Feed';
import { userService } from 'common/services/user.service';
import { RetailerType } from './entities';
import { RoutesAvailability } from './routes/routes-availability';

export interface NavItem {
  title: string;
  path?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: OverridableComponent<SvgIconTypeMap<any, 'svg'>> & {
    muiName: string;
  };
  nested?: Array<NavItem>;
  checkAvailability: () => boolean;
  demoBlur: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getMainNavConfig = (t: TFunction): Array<NavItem> => [
  {
    title: 'Overview',
    path: '/',
    icon: userService.ensureRetailer(RetailerType.Walmart) ? LayersIcon : ViewListIcon,
    checkAvailability: () => RoutesAvailability.Overview() && userService.ensureRetailer(RetailerType.Walmart),
    demoBlur: true,
  },
  {
    title: 'Core Actions',
    checkAvailability: () => true,
    demoBlur: false,
    icon: HubIcon,
    nested: [
      {
        title: 'Overview',
        path: '/overview',
        icon: QueryStatsIcon,
        checkAvailability: () => RoutesAvailability.OverviewPage(),
        demoBlur: false,
      },
      {
        title: 'Products',
        path: '/',
        icon: userService.ensureRetailer(RetailerType.Walmart) ? LayersIcon : ViewListIcon,
        checkAvailability: () => RoutesAvailability.Overview() && !userService.ensureRetailer(RetailerType.Walmart),
        demoBlur: true,
      },
      {
        title: 'Products',
        path: '/content',
        icon: ViewListIcon,
        checkAvailability: () => RoutesAvailability.ContentManagement(),
        demoBlur: true,
      },
      {
        title: 'Categories',
        path: '/categories-settings',
        icon: CategoryIcon,
        checkAvailability: () => RoutesAvailability.CategoriesSettings(),
        demoBlur: true,
      },
      {
        title: 'Keywords Tool',
        path: '/keywords-tool',
        icon: KeyIcon,
        checkAvailability: () => RoutesAvailability.KeywordsTool(),
        demoBlur: true,
      },

      {
        title: 'Add XLSX',
        path: '/add-xlsx',
        icon: AddCircleOutlineIcon,
        checkAvailability: () => RoutesAvailability.AddXLSX(),
        demoBlur: true,
      },
      {
        title: 'Add Product IDs',
        path: '/add-tcins',
        icon: AddCircleOutlineIcon,
        checkAvailability: () => RoutesAvailability.AddTCINs(),
        demoBlur: true,
      },
      {
        title: 'Add Product IDs',
        path: '/add-asins',
        icon: AddCircleOutlineIcon,
        checkAvailability: () => RoutesAvailability.AddASINs(),
        demoBlur: true,
      },
      {
        title: 'Add Product IDs',
        path: '/add-skus',
        icon: AddCircleOutlineIcon,
        checkAvailability: () => RoutesAvailability.AddSKUs(),
        demoBlur: true,
      },
      {
        title: userService.ensureRetailer(RetailerType.Walmart) ? 'Walmart Change Tracking' : 'Target Change Tracking',
        path: '/change-tracking',
        icon: ManageHistoryIcon,
        checkAvailability: () => RoutesAvailability.ChangeTracking(),
        demoBlur: false,
      },
      {
        title: 'PDP Generation',
        path: '/pdp-gen',
        icon: AutoFixHighIcon,
        checkAvailability: () => RoutesAvailability.PDPGeneration(),
        demoBlur: true,
      },
      {
        title: 'Quick Start',
        path: '/start',
        icon: PreviewIcon,
        checkAvailability: () => true,
        demoBlur: true,
      },
    ],
  },

  {
    title: 'Insights',
    checkAvailability: () => true,
    demoBlur: false,
    icon: InsightsIcon,
    nested: [
      {
        title: 'AI',
        icon: AutoAwesomeIcon,
        checkAvailability: () => true,
        demoBlur: false,
        nested: [
          {
            title: 'Queue',
            path: '/ai-queue',
            icon: QueueIcon,
            checkAvailability: () => RoutesAvailability.AIQueue(),
            demoBlur: false,
          },
          {
            title: 'Instructions',
            path: '/ai-instructions',
            icon: AltRouteIcon,
            checkAvailability: () => RoutesAvailability.AIInstructions(),
            demoBlur: false,
          },
          {
            title: 'Swatch Creator',
            path: '/swatch-creator',
            icon: ImageIcon,
            checkAvailability: () => RoutesAvailability.SwatchCreator(),
            demoBlur: false,
          },
          {
            title: 'Warning Settings',
            path: '/warning-rules',
            icon: WarningIcon,
            checkAvailability: () => RoutesAvailability.WarningRules(),
            demoBlur: false,
          },
          {
            title: 'Retailer AI Settings',
            path: '/ai-settings',
            icon: SettingsSuggestIcon,
            checkAvailability: () => RoutesAvailability.AISettings(),
            demoBlur: false,
          },
        ],
      },
      {
        title: 'Performance',
        icon: ShowChartIcon,
        checkAvailability: () => RoutesAvailability.Performance(),
        demoBlur: false,
        nested: [
          {
            title: 'Leaderboard',
            path: '/leaderboard',
            icon: LeaderboardIcon,
            checkAvailability: () => RoutesAvailability.Leaderboard(),
            demoBlur: false,
          },
          {
            title: 'AI',
            icon: AutoAwesomeIcon,
            checkAvailability: () => true,
            demoBlur: false,
            nested: [
              {
                title: 'Benchmark',
                path: '/ai-benchmark',
                icon: InsertChartIcon,
                checkAvailability: () => RoutesAvailability.AIBenchmark(),
                demoBlur: false,
              },
              {
                title: 'Benchmark Stats ',
                path: '/ai-benchmark-stats',
                icon: DonutLargeIcon,
                checkAvailability: () => RoutesAvailability.AIBenchmarkStats(),
                demoBlur: false,
              },
            ],
          },
          {
            title: 'Compare',
            icon: GroupsIcon,
            checkAvailability: () => true,
            demoBlur: false,
            nested: [
              {
                title: 'Benchmark',
                path: '/compare-benchmark',
                icon: InsertChartIcon,
                checkAvailability: () => RoutesAvailability.CompareBenchmark(),
                demoBlur: false,
              },
              {
                title: 'Benchmark Stats ',
                path: '/compare-benchmark-stats',
                icon: DonutLargeIcon,
                checkAvailability: () => RoutesAvailability.CompareBenchmarkStats(),
                demoBlur: false,
              },
            ],
          },
        ],
      },
      {
        title: 'Comments',
        path: '/comments',
        icon: NotesIcon,
        checkAvailability: () => RoutesAvailability.Comments(),
        demoBlur: true,
      },
    ],
  },

  {
    title: 'Support Tools',
    checkAvailability: () => true,
    demoBlur: false,
    icon: SupportAgentIcon,
    nested: [
      {
        title: 'Support',
        path: '/support',
        icon: ForumIcon,
        checkAvailability: () => RoutesAvailability.Support(),
        demoBlur: true,
      },
      {
        title: 'Settings',
        path: '/settings/team',
        icon: SettingsIcon,
        checkAvailability: () => RoutesAvailability.Settings(),
        demoBlur: true,
      },
    ],
  },
];

export const getVideoNavConfig = (): Array<NavItem> => [
  {
    title: 'Dashboard',
    path: '/',
    icon: ViewListIcon,
    checkAvailability: () => true,
    demoBlur: false,
  },
  {
    title: 'AI Chat',
    path: '/vc-chat',
    icon: ForumIcon,
    checkAvailability: () => true,
    demoBlur: false,
  },
  {
    title: 'Create Content',
    path: '/video-creation',
    icon: AutoAwesomeIcon,
    checkAvailability: () => true,
    demoBlur: false,
  },
  {
    title: 'Assets Playground',
    path: '/assets-playground',
    icon: ColorLensIcon,
    checkAvailability: () => true,
    demoBlur: false,
  },
  {
    title: 'Trends',
    path: '/trends',
    icon: TrendingUpIcon,
    checkAvailability: () => true,
    demoBlur: true,
  },
  {
    title: 'Channel Posts',
    path: '/channel-posts',
    icon: FeedIcon,
    checkAvailability: () => true,
    demoBlur: true,
  },
  {
    title: 'Settings',
    checkAvailability: () => true,
    demoBlur: false,
    icon: SettingsIcon,
    nested: [
      {
        title: 'Users',
        path: '/settings/users',
        icon: GroupIcon,
        checkAvailability: () => true,
        demoBlur: true,
      },
      {
        title: 'Channels',
        path: '/settings/channels',
        icon: ChecklistIcon,
        checkAvailability: () => RoutesAvailability.VideoChannelsSettings(),
        demoBlur: true,
      },
      {
        title: 'Brands',
        path: '/settings/brands',
        icon: BusinessIcon,
        checkAvailability: () => true,
        demoBlur: true,
      },
    ],
  },
];
